import { useTranslation } from 'react-i18next';

import { formatValue } from '@/entities/dashboard';
import type { ConsignmentListType } from '@/entities/dashboard/mapping';
import { MAP_REPORT_CONFIG, type MapReport } from '@/features/map-layers';
import type { MapCluster } from '@/features/map-grouping';

import type { MapDataFilters } from '../model/map-provider.interface';
import { ClusterConsignments } from './cluster-consignments';
import { LocationPopupBreakdown } from './location-popup-breakdown';

interface ClusterPopupProps {
  cluster: MapCluster;
  /** The slice that was clicked — legacy titles the popup after it. */
  report: MapReport;
  color: string;
  /** Weeks back the time machine is showing; 0 is the present. */
  weeksAgo: number;
  dimensionLabel?: string;
  dataFilters?: MapDataFilters;
}

/** Released cargo opens the pie; the other three list their consignments, as in legacy. */
const consignmentListType = (report: MapReport): ConsignmentListType | undefined =>
  report === 'REL' ? undefined : (report as ConsignmentListType);

export function ClusterPopup({ cluster, report, color, weeksAgo, dimensionLabel, dataFilters }: ClusterPopupProps) {
  const { t } = useTranslation();
  const locationIds = cluster.locations.map((location) => location.id);
  const listType = consignmentListType(report);

  return (
    <div className="min-w-[300px] max-w-[400px] p-3">
      <div className="mb-3 border-b pb-2">
        <h3 className="flex items-center gap-2 font-semibold text-base">
          <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />
          {t(MAP_REPORT_CONFIG[report].labelKey)}
        </h3>
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-muted-foreground text-sm">
            {dimensionLabel ? `${dimensionLabel}: ${cluster.name}` : cluster.name}
          </p>
          {/* Rounding to whole tonnes printed "0" for every cluster under half a tonne. */}
          <p className="shrink-0 font-bold text-base text-primary">{formatValue(cluster.values[report], 'W')}</p>
        </div>
      </div>

      {listType && (
        <ClusterConsignments locationIds={locationIds} type={listType} weeksAgo={weeksAgo} dataFilters={dataFilters} />
      )}

      {report === 'REL' && <LocationPopupBreakdown locationIds={locationIds} dataFilters={dataFilters} />}
    </div>
  );
}
